import React, { useState, useEffect } from 'react';
import {
    Card, Select, Row, Col, Statistic, Tag, Table,
    Badge, Progress, Spin, message, Alert, Tooltip
} from 'antd';
import {
    WarningOutlined, CheckCircleOutlined, ClockCircleOutlined,
    BarChartOutlined, FireOutlined
} from '@ant-design/icons';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartTooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import MeService from '../../services/MeService';

const { Option } = Select;

// ── Constants ─────────────────────────────────────────────────────────────
const REGIONS = ['Hhohho', 'Manzini', 'Lubombo', 'Shiselweni'];

const TRAFFIC_LIGHT = (pct) => {
    if (pct >= 75) return { color:'#52c41a', label:'On Track',    icon:'🟢' };
    if (pct >= 50) return { color:'#fa8c16', label:'At Risk',     icon:'🟡' };
    if (pct >= 25) return { color:'#f5222d', label:'Off Track',   icon:'🔴' };
    return              { color:'#8c8c8c', label:'Not Started',  icon:'⚫' };
};

const FRAMEWORK_TYPE_COLORS = {
    standard:  '#1890ff',
    workplan:  '#52c41a',
    strategy:  '#722ed1',
    sdg:       '#13c2c2',
    policy:    '#fa8c16',
    programme: '#eb2f96',
    other:     '#8c8c8c',
};

// ── Traffic Light Card ────────────────────────────────────────────────────
function TrafficLightCard({ assignment }) {
    const pct  = parseFloat(assignment.completion_pct || 0);
    const tl   = TRAFFIC_LIGHT(pct);
    const days = assignment.target_completion
        ? Math.ceil((new Date(assignment.target_completion) - new Date()) / 86400000)
        : null;

    return (
        <Card size="small" style={{
            borderLeft:`4px solid ${tl.color}`,
            marginBottom:8,
            background: pct >= 75 ? '#f6ffed' : pct >= 50 ? '#fff7e6' : pct >= 25 ? '#fff2f0' : '#fafafa'
        }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ fontSize:22, flexShrink:0 }}>{tl.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:12, color:'#1f3864',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {assignment.framework_name}
                    </div>
                    <div style={{ fontSize:11, color:'#888',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {assignment.facility_name}
                    </div>
                    <Progress size="small" percent={pct} showInfo={false}
                        strokeColor={tl.color} style={{ margin:'3px 0 0' }}/>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontWeight:800, fontSize:16, color:tl.color }}>{pct.toFixed(0)}%</div>
                    {days !== null && (
                        <div style={{ fontSize:10, color: days < 0 ? '#f5222d' : days < 30 ? '#fa8c16' : '#888' }}>
                            {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}

// ── Heatmap Component ─────────────────────────────────────────────────────
function ComplianceHeatmap({ data }) {
    if (!data || data.length === 0) return (
        <div style={{ textAlign:'center', padding:40, color:'#888' }}>No heatmap data available</div>
    );

    // Build matrix: rows = facilities, cols = frameworks
    const facilities = [...new Map(data.map(d => [d.facility_id, d.facility_name])).entries()]
        .map(([id, name]) => ({ id, name }));
    const frameworks = [...new Map(data.map(d => [d.framework_id, { name:d.framework_name, code:d.framework_code }])).entries()]
        .map(([id, fw]) => ({ id, ...fw }));

    const getCell = (facId, fwId) => {
        const row = data.find(d => String(d.facility_id)===String(facId) && String(d.framework_id)===String(fwId));
        return row ? parseFloat(row.completion_pct || 0) : null;
    };

    const cellBg = (pct) => {
        if (pct === null) return '#f5f5f5';
        if (pct >= 75)   return '#52c41a';
        if (pct >= 50)   return '#fa8c16';
        if (pct >= 25)   return '#f5222d';
        return '#d9d9d9';
    };

    return (
        <div style={{ overflowX:'auto' }}>
            <table style={{ borderCollapse:'collapse', fontSize:11, width:'100%' }}>
                <thead>
                    <tr style={{ background:'#1f3864' }}>
                        <th style={{ padding:'6px 10px', color:'#fff', textAlign:'left',
                            minWidth:150, position:'sticky', left:0, background:'#1f3864' }}>
                            Facility
                        </th>
                        {frameworks.map(fw => (
                            <th key={fw.id} style={{ padding:'4px 6px', color:'#fff',
                                textAlign:'center', minWidth:80, fontSize:10 }}>
                                {fw.code || fw.name.slice(0,8)}
                            </th>
                        ))}
                        <th style={{ padding:'6px 8px', color:'#fff', textAlign:'center', minWidth:60 }}>
                            Avg
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {facilities.map((fac, fi) => {
                        const cells = frameworks.map(fw => getCell(fac.id, fw.id));
                        const validCells = cells.filter(c => c !== null);
                        const avg = validCells.length
                            ? Math.round(validCells.reduce((a,b) => a+b, 0) / validCells.length)
                            : null;
                        return (
                            <tr key={fac.id} style={{ background: fi%2 ? '#fafafa' : '#fff' }}>
                                <td style={{ padding:'4px 10px', fontWeight:600,
                                    position:'sticky', left:0, background: fi%2?'#fafafa':'#fff',
                                    borderRight:'2px solid #e0eaff', fontSize:11,
                                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                    maxWidth:150 }}>
                                    {fac.name}
                                </td>
                                {cells.map((pct, ci) => (
                                    <td key={ci} style={{ padding:'4px 6px', textAlign:'center',
                                        background: cellBg(pct),
                                        color: pct !== null && pct > 0 ? '#fff' : '#bbb',
                                        fontWeight:700, fontSize:11 }}>
                                        {pct !== null ? `${pct.toFixed(0)}%` : '—'}
                                    </td>
                                ))}
                                <td style={{ padding:'4px 8px', textAlign:'center', fontWeight:800,
                                    color: avg !== null ? cellBg(avg) : '#bbb', fontSize:12 }}>
                                    {avg !== null ? `${avg}%` : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ── Main Dashboard ────────────────────────────────────────────────────────
export default function MeDashboard() {
    const [dashboard,   setDashboard]   = useState(null);
    const [heatmap,     setHeatmap]     = useState([]);
    const [trend,       setTrend]       = useState([]);
    const [overdue,     setOverdue]     = useState([]);
    const [summary,     setSummary]     = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [filterFw,    setFilterFw]    = useState('');
    const [filterRegion,setFilterRegion]= useState('');
    const [frameworks,  setFrameworks]  = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const filters = {
                ...(filterFw     ? { framework_id: filterFw }   : {}),
                ...(filterRegion ? { region: filterRegion }      : {}),
            };

            const [dashRes, heatRes, trendRes, overdueRes, summaryRes, fwRes] = await Promise.all([
                MeService.getDashboard(filters),
                MeService.getHeatmap(filters),
                MeService.getTrend(filters),
                MeService.getOverdueActions(filters),
                MeService.getProgressSummary(filters),
                MeService.getFrameworks({ status:'active' }),
            ]);

            setDashboard(dashRes.data?.data   || null);
            setHeatmap(heatRes.data?.data      || []);
            setTrend(trendRes.data?.data        || []);
            setOverdue(overdueRes.data?.data   || []);
            setSummary(summaryRes.data?.data   || []);
            setFrameworks(fwRes.data?.data      || []);
        } catch(e) { message.error('Failed to load dashboard'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filterFw, filterRegion]); // eslint-disable-line

    const kpis     = dashboard?.kpis     || {};
    const byType   = dashboard?.by_type  || [];
    const recent   = dashboard?.recent   || [];

    // Traffic lights — sort by completion asc so worst appear first
    const trafficLights = [...summary]
        .sort((a,b) => parseFloat(a.completion_pct||0) - parseFloat(b.completion_pct||0))
        .slice(0, 12);

    // Trend chart data
    const trendChartData = trend.map(t => ({
        period:       t.reporting_period,
        achievement:  parseFloat(t.avg_achievement || 0).toFixed(1),
        met:          parseInt(t.met_count || 0),
        not_met:      parseInt(t.not_met_count || 0),
    }));

    // Overdue table columns
    const overdueColumns = [
        { title:'Indicator', dataIndex:'indicator_title', ellipsis:true,
            render:(v, rec) => (
                <div>
                    <div style={{ fontWeight:600, fontSize:12 }}>{v}</div>
                    <div style={{ fontSize:11, color:'#888' }}>{rec.framework_name}</div>
                </div>
            )
        },
        { title:'Facility', dataIndex:'facility_name', width:140, ellipsis:true,
            render:(v, rec) => (
                <div>
                    <div style={{ fontSize:12 }}>{v}</div>
                    {rec.region && <div style={{ fontSize:11, color:'#888' }}>{rec.region}</div>}
                </div>
            )
        },
        { title:'Action Owner', dataIndex:'corrective_action_owner', width:130, ellipsis:true },
        { title:'Overdue', dataIndex:'days_overdue', width:90, align:'center',
            render: v => (
                <Tag color="red" style={{ fontWeight:700 }}>{v} day{v!==1?'s':''}</Tag>
            )
        },
        { title:'Action', dataIndex:'corrective_action', ellipsis:true,
            render: v => <span style={{ fontSize:11, color:'#555' }}>{v}</span>
        },
    ];

    // Summary table columns
    const summaryColumns = [
        { title:'Framework', dataIndex:'framework_name', ellipsis:true,
            render:(v, rec) => (
                <div>
                    <Tag color={FRAMEWORK_TYPE_COLORS[rec.framework_type] || '#888'}
                        style={{ fontSize:10 }}>{rec.framework_code}</Tag>
                    <span style={{ fontSize:12, marginLeft:4 }}>{v}</span>
                </div>
            )
        },
        { title:'Facility', dataIndex:'facility_name', width:150, ellipsis:true,
            render:(v, rec) => (
                <div>
                    <div style={{ fontSize:12 }}>{v}</div>
                    {rec.region && <div style={{ fontSize:11, color:'#888' }}>{rec.region}</div>}
                </div>
            )
        },
        { title:'Progress', dataIndex:'completion_pct', width:130,
            render:(v, rec) => {
                const pct = parseFloat(v || 0);
                const tl  = TRAFFIC_LIGHT(pct);
                return (
                    <div>
                        <Progress size="small" percent={pct} showInfo={false}
                            strokeColor={tl.color}/>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:10 }}>
                            <span style={{ color:tl.color }}>{pct.toFixed(1)}%</span>
                            <span style={{ color:'#888' }}>
                                {rec.met_count}/{rec.total_indicators} met
                            </span>
                        </div>
                    </div>
                );
            }
        },
        { title:'Status', dataIndex:'assignment_status', width:110,
            render:(v, rec) => {
                const tl = TRAFFIC_LIGHT(parseFloat(rec.completion_pct||0));
                return <span>{tl.icon} {v}</span>;
            }
        },
        { title:'Pending ✓', dataIndex:'pending_verification', width:90, align:'center',
            render: v => v > 0
                ? <Tag color="orange">{v}</Tag>
                : <CheckCircleOutlined style={{ color:'#52c41a' }}/>
        },
        { title:'Overdue ⚠', dataIndex:'overdue_actions', width:90, align:'center',
            render: v => v > 0
                ? <Tag color="red">{v}</Tag>
                : <span style={{ color:'#52c41a' }}>—</span>
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin:0 }}>📊 M&amp;E Dashboard</h4>
                    <small className="text-muted">Implementation progress, trends and overdue actions</small>
                </div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8 }}>
                    <Select value={filterFw} onChange={setFilterFw}
                        style={{ width:200 }} allowClear placeholder="All frameworks">
                        {frameworks.map(f => (
                            <Option key={f.id} value={f.id}>{f.code} — {f.name}</Option>
                        ))}
                    </Select>
                    <Select value={filterRegion} onChange={setFilterRegion}
                        style={{ width:140 }} allowClear placeholder="All regions">
                        {REGIONS.map(r => <Option key={r} value={r}>{r}</Option>)}
                    </Select>
                </div>
            </div>

            <div className="page-pad">
                {loading ? (
                    <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
                ) : (
                    <>
                        {/* KPI Strip */}
                        <Row gutter={12} style={{ marginBottom:16 }}>
                            {[
                                { label:'Active Frameworks',    val:kpis.total_frameworks    || 0, color:'#1f3864' },
                                { label:'Total Assignments',    val:kpis.total_assignments   || 0, color:'#1890ff' },
                                { label:'Avg Completion',       val:`${parseFloat(kpis.avg_completion||0).toFixed(1)}%`, color:'#52c41a' },
                                { label:'Indicators Tracked',   val:kpis.total_indicators    || 0, color:'#7c3aed' },
                                { label:'Met Indicators',       val:kpis.met_indicators      || 0, color:'#52c41a' },
                                { label:'Pending Verification', val:kpis.pending_verification|| 0, color:'#fa8c16' },
                                { label:'Overdue Actions',      val:kpis.overdue_actions     || 0, color:'#f5222d' },
                                { label:'Delayed Assignments',  val:kpis.delayed_assignments || 0, color:'#f5222d' },
                            ].map(k => (
                                <Col key={k.label} xs={12} sm={6} md={3}>
                                    <Card size="small" style={{ textAlign:'center',
                                        borderLeft:`4px solid ${k.color}`, marginBottom:8 }}>
                                        <div style={{ fontSize:20, fontWeight:800, color:k.color }}>{k.val}</div>
                                        <div style={{ fontSize:10, color:'#888' }}>{k.label}</div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* Overdue alert */}
                        {overdue.length > 0 && (
                            <Alert type="error" showIcon icon={<FireOutlined/>}
                                style={{ marginBottom:16 }}
                                message={`${overdue.length} overdue corrective action${overdue.length>1?'s':''}`}
                                description="These actions have passed their deadline and require immediate attention."
                            />
                        )}

                        {/* Row 1: Traffic Lights + By Type */}
                        <Row gutter={16} style={{ marginBottom:16 }}>
                            <Col xs={24} md={16}>
                                <Card size="small" title={
                                    <span style={{ fontWeight:700 }}>
                                        🚦 Implementation Traffic Lights
                                        <span style={{ fontSize:11, color:'#888', marginLeft:8, fontWeight:400 }}>
                                            Worst performing first
                                        </span>
                                    </span>
                                } style={{ height:'100%' }}>
                                    {trafficLights.length === 0 ? (
                                        <div style={{ textAlign:'center', padding:30, color:'#888' }}>
                                            No assignments found
                                        </div>
                                    ) : (
                                        <div style={{ maxHeight:320, overflowY:'auto' }}>
                                            {trafficLights.map(a => (
                                                <TrafficLightCard key={a.assignment_id} assignment={a}/>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <Card size="small" title={
                                    <span style={{ fontWeight:700 }}>📋 By Framework Type</span>
                                } style={{ height:'100%' }}>
                                    {byType.length === 0 ? (
                                        <div style={{ textAlign:'center', padding:30, color:'#888' }}>No data</div>
                                    ) : byType.map(t => (
                                        <div key={t.type} style={{ marginBottom:12 }}>
                                            <div style={{ display:'flex', justifyContent:'space-between',
                                                marginBottom:4, fontSize:12 }}>
                                                <span style={{ fontWeight:600, textTransform:'capitalize' }}>
                                                    {t.type}
                                                </span>
                                                <span style={{ color:'#888' }}>
                                                    {t.count} · {parseFloat(t.avg_completion||0).toFixed(0)}%
                                                </span>
                                            </div>
                                            <Progress size="small"
                                                percent={parseFloat(t.avg_completion || 0)}
                                                strokeColor={FRAMEWORK_TYPE_COLORS[t.type] || '#1890ff'}
                                                showInfo={false}/>
                                        </div>
                                    ))}
                                </Card>
                            </Col>
                        </Row>

                        {/* Row 2: Trend Chart */}
                        {trendChartData.length > 0 && (
                            <Card size="small" title={
                                <span style={{ fontWeight:700 }}>📈 Achievement Trend Over Time</span>
                            } style={{ marginBottom:16 }}>
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={trendChartData}
                                        margin={{ top:5, right:20, left:0, bottom:5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                        <XAxis dataKey="period" tick={{ fontSize:11 }}/>
                                        <YAxis tick={{ fontSize:11 }} domain={[0, 100]}
                                            tickFormatter={v => `${v}%`}/>
                                        <RechartTooltip formatter={(v, n) =>
                                            [`${parseFloat(v).toFixed(1)}%`, n]}/>
                                        <Legend/>
                                        <Line type="monotone" dataKey="achievement"
                                            name="Avg Achievement %" stroke="#1890ff"
                                            strokeWidth={2} dot={{ r:4 }}/>
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card>
                        )}

                        {/* Row 3: Heatmap */}
                        <Card size="small" title={
                            <span style={{ fontWeight:700 }}>
                                🗺️ Compliance Heatmap
                                <span style={{ fontSize:11, color:'#888', marginLeft:8, fontWeight:400 }}>
                                    Facility × Framework completion %
                                </span>
                            </span>
                        } style={{ marginBottom:16 }}>
                            {/* Legend */}
                            <div style={{ display:'flex', gap:12, marginBottom:8, flexWrap:'wrap' }}>
                                {[
                                    { label:'≥75% On Track',   color:'#52c41a' },
                                    { label:'50–74% At Risk',  color:'#fa8c16' },
                                    { label:'25–49% Off Track',color:'#f5222d' },
                                    { label:'<25% Not Started',color:'#d9d9d9' },
                                    { label:'Not Assigned',    color:'#f5f5f5' },
                                ].map(l => (
                                    <div key={l.label} style={{ display:'flex', alignItems:'center', gap:4, fontSize:11 }}>
                                        <div style={{ width:14, height:14, background:l.color, borderRadius:2 }}/>
                                        <span style={{ color:'#555' }}>{l.label}</span>
                                    </div>
                                ))}
                            </div>
                            <ComplianceHeatmap data={heatmap}/>
                        </Card>

                        {/* Row 4: Overdue actions */}
                        {overdue.length > 0 && (
                            <Card size="small" title={
                                <span style={{ fontWeight:700, color:'#f5222d' }}>
                                    ⚠️ Overdue Corrective Actions ({overdue.length})
                                </span>
                            } style={{ marginBottom:16 }}>
                                <Table
                                    size="small"
                                    dataSource={overdue}
                                    rowKey="id"
                                    columns={overdueColumns}
                                    pagination={{ pageSize:5, size:'small' }}
                                />
                            </Card>
                        )}

                        {/* Row 5: Full summary table */}
                        <Card size="small" title={
                            <span style={{ fontWeight:700 }}>
                                📋 Implementation Summary — All Assignments
                            </span>
                        }>
                            <Table
                                size="small"
                                dataSource={summary}
                                rowKey="assignment_id"
                                columns={summaryColumns}
                                pagination={{ pageSize:15, showSizeChanger:true,
                                    showTotal: t => `${t} assignments` }}
                                expandable={{
                                    expandedRowRender: rec => (
                                        <div style={{ padding:'6px 16px', fontSize:12 }}>
                                            <Row gutter={16}>
                                                {[
                                                    { label:'Total Indicators', val:rec.total_indicators },
                                                    { label:'Met',              val:rec.met_count,          color:'#52c41a' },
                                                    { label:'In Progress',      val:rec.in_progress_count,  color:'#1890ff' },
                                                    { label:'Not Met',          val:rec.not_met_count,      color:'#f5222d' },
                                                    { label:'Not Started',      val:rec.not_started_count,  color:'#888' },
                                                    { label:'Verified',         val:rec.verified_count,     color:'#52c41a' },
                                                ].map(k => (
                                                    <Col key={k.label}>
                                                        <div style={{ textAlign:'center' }}>
                                                            <div style={{ fontWeight:800, fontSize:16,
                                                                color:k.color||'#1f3864' }}>{k.val}</div>
                                                            <div style={{ fontSize:10, color:'#888' }}>{k.label}</div>
                                                        </div>
                                                    </Col>
                                                ))}
                                                {rec.target_completion && (
                                                    <Col>
                                                        <div style={{ textAlign:'center' }}>
                                                            <div style={{ fontWeight:700, fontSize:12, color:'#888' }}>
                                                                Target
                                                            </div>
                                                            <div style={{ fontSize:12 }}>
                                                                {new Date(rec.target_completion).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    ),
                                }}
                            />
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}
